import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Badge,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Collapse,
  Typography,
} from '@mui/material';
import {
  Warning as IncidentsIcon,
  Dashboard as DashboardIcon,
  ViewModule as ViewsIcon,
  People as HeadcounterIcon,
  PersonSearch as MissingIcon,
  Map as MapIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Groups as TeamIcon,
  HelpOutlined as HelpIcon,
} from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { getIncidentSummary } from '../../Services/IncidentService';
import type { IncidentSummary } from '../../Types/Incident';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 56;
const NAV_COUNT_REFRESH_MS = 30_000;

/** Extract the all-scope not-accounted count from an IncidentSummary. */
export const getNavNotAccountedCount = (summary: IncidentSummary | null): number => {
  if (!summary) return 0;
  if (summary.scopeCounts) {
    return summary.scopeCounts.needsAttentionCount ?? summary.scopeCounts.fullRosterCounts.notAccounted;
  }
  return summary.notAccounted ?? 0;
};

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
  /** Custom primary content for ListItemText */
  renderLabel?: React.ReactNode;
  /** Accessible label override */
  ariaLabel?: string;
}

interface SidebarNavProps {
  collapsed: boolean;
  onToggle: () => void;
}

const SidebarNav: React.FC<SidebarNavProps> = ({ collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [subMenuOpen, setSubMenuOpen] = useState(true);

  // Extract incidentId from the current path
  const incidentMatch = location.pathname.match(/^\/incidents\/([^/]+)/);
  const activeIncidentId = incidentMatch ? incidentMatch[1] : null;
  const isInIncident = !!activeIncidentId && activeIncidentId !== 'new';

  // ── Not Accounted count for the active incident ──
  const [notAccountedCount, setNotAccountedCount] = useState(0);

  const fetchNotAccountedCount = useCallback(async (id: string) => {
    try {
      const { summary } = await getIncidentSummary(id);
      setNotAccountedCount(getNavNotAccountedCount(summary));
    } catch {
      // Silently ignore — keep previous count
    }
  }, []);

  useEffect(() => {
    if (!activeIncidentId || activeIncidentId === 'new') {
      setNotAccountedCount(0);
      return;
    }
    fetchNotAccountedCount(activeIncidentId);
    const timer = setInterval(() => fetchNotAccountedCount(activeIncidentId), NAV_COUNT_REFRESH_MS);

    // Listen for status-change events from other components so the count
    // updates immediately instead of waiting for the next polling interval.
    const onStatusChanged = () => fetchNotAccountedCount(activeIncidentId);
    window.addEventListener('eeas:statusChanged', onStatusChanged);

    return () => {
      clearInterval(timer);
      window.removeEventListener('eeas:statusChanged', onStatusChanged);
    };
  }, [activeIncidentId, fetchNotAccountedCount]);

  // Auto-expand sub-menu when entering an incident
  useEffect(() => {
    if (isInIncident) setSubMenuOpen(true);
  }, [isInIncident]);

  const navItems: NavItem[] = [
    {
      label: 'Incidents',
      path: '/',
      icon: <IncidentsIcon />,
      children: isInIncident
        ? [
            { label: 'Incident Dashboard', path: `/incidents/${activeIncidentId}/incidentdashboard`, icon: <DashboardIcon /> },
            { label: 'Roster Views', path: `/incidents/${activeIncidentId}/views`, icon: <ViewsIcon /> },
            { label: 'Headcount', path: `/incidents/${activeIncidentId}/headcount`, icon: <HeadcounterIcon /> },
            {
              label: 'Not Accounted',
              path: `/incidents/${activeIncidentId}/unaccounted`,
              icon: notAccountedCount > 0
                ? <Badge variant="dot" color="error" overlap="circular"><MissingIcon /></Badge>
                : <MissingIcon />,
              ariaLabel: notAccountedCount > 0 ? `Not Accounted, ${notAccountedCount} people` : undefined,
              renderLabel: notAccountedCount > 0 ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Not Accounted</span>
                  <Typography
                    component="span"
                    sx={{ color: appColors.status.error, fontWeight: 800, fontVariantNumeric: 'tabular-nums', fontSize: 'inherit' }}
                  >
                    ({notAccountedCount})
                  </Typography>
                </Box>
              ) : undefined,
            },
            { label: 'Team', path: `/incidents/${activeIncidentId}/team`, icon: <TeamIcon /> },
            { label: 'Facility Map', path: `/incidents/${activeIncidentId}/facility-map`, icon: <MapIcon /> },
          ]
        : undefined,
    },
    { label: 'Reports', path: '/reports', icon: <ReportsIcon /> },
    { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
    { label: 'Help', path: '/help', icon: <HelpIcon /> },
  ];

  const isActive = (path: string): boolean => {
    if (path === '/') {
      // Incidents nav: match root and /incidents/* but NOT /incidents/:id/report (that's Reports)
      if (location.pathname === '/' || location.pathname.startsWith('/incidents')) {
        return !location.pathname.match(/\/incidents\/[^/]+\/report$/);
      }
      return false;
    }
    if (path === '/reports') {
      return location.pathname === '/reports' || location.pathname.match(/\/incidents\/[^/]+\/report$/) !== null;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isChildActive = (path: string): boolean => {
    if (location.pathname === path || location.pathname.startsWith(path + '?')) return true;
    // Review & Submit page is part of Headcount workflow
    if (path.endsWith('/headcount') && location.pathname.endsWith('/review')) return true;
    // Views page is part of Headcount workflow
    if (path.endsWith('/views') && location.pathname.endsWith('/views')) return true;
    return false;
  };

  return (
    <Box
      component="nav"
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        minWidth: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        height: '100%',
        bgcolor: appColors.sidebar.bg,
        borderRight: `1px solid ${appColors.sidebar.border}`,
        display: 'flex',
        flexDirection: 'column',
        color: appColors.sidebar.text,
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Collapse toggle */}
      <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: collapsed ? 0 : 1, py: 1 }}>
        <IconButton onClick={onToggle} size="small" sx={{ color: appColors.sidebar.textMuted }}>
          {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Nav Items */}
      <List sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: collapsed ? 0.5 : 1, pt: 0 }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <React.Fragment key={item.label}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    selected={active && !item.children}
                    sx={{
                      borderRadius: 0,
                      borderLeft: '4px solid transparent',
                      minHeight: 48,
                      py: 1.25,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      color: appColors.sidebar.text,
                      transition: 'all 150ms ease-in-out',
                      '&.Mui-selected': {
                        bgcolor: appColors.sidebar.selectedBg,
                        borderLeft: `4px solid ${appColors.sidebar.activeBorder}`,
                        color: appColors.sidebar.activeText,
                        '&:hover': { bgcolor: appColors.sidebar.selectedHover },
                      },
                      '&:hover': { bgcolor: appColors.sidebar.itemHover },
                      ...(active && item.children
                        ? { color: appColors.sidebar.activeText, borderLeft: `4px solid ${appColors.sidebar.activeBorder}`, bgcolor: appColors.sidebar.selectedBg }
                        : {}),
                    }}
                  >
                    <ListItemIcon sx={{ color: 'inherit', minWidth: collapsed ? 0 : 36, justifyContent: 'center' }}>
                      {item.icon}
                    </ListItemIcon>
                    {!collapsed && (
                      <ListItemText
                        primary={item.label}
                        slotProps={{ primary: { sx: { fontSize: '0.8125rem', fontWeight: 600, color: 'inherit' } } }}
                      />
                    )}
                    {!collapsed && item.children && isInIncident && (
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setSubMenuOpen((o) => !o); }}
                        sx={{ color: 'inherit', p: 0.25, ml: 0.5 }}
                      >
                        {subMenuOpen ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                      </IconButton>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* Context-sensitive sub-menu */}
              {item.children && !collapsed && (
                <Collapse in={isInIncident && subMenuOpen} timeout="auto" unmountOnExit>
                  <List disablePadding sx={{ pl: 2.5 }}>
                    {item.children.map((child) => {
                      const childActive = isChildActive(child.path);
                      return (
                        <ListItem key={child.label} disablePadding sx={{ mb: 0.25 }}>
                          <ListItemButton
                            onClick={() => navigate(child.path)}
                            selected={childActive}
                            aria-label={child.ariaLabel}
                            sx={{
                              borderRadius: 0,
                              borderLeft: '4px solid transparent',
                              minHeight: 44,
                              py: 1,
                              color: appColors.sidebar.text,
                              transition: 'all 150ms ease-in-out',
                              '&.Mui-selected': {
                                bgcolor: appColors.sidebar.selectedBg,
                                borderLeft: `4px solid ${appColors.sidebar.activeBorder}`,
                                color: appColors.sidebar.activeText,
                                '&:hover': { bgcolor: appColors.sidebar.selectedHover },
                              },
                              '&:hover': { bgcolor: appColors.sidebar.itemHover },
                            }}
                          >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 30 }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.renderLabel ?? child.label}
                              slotProps={{ primary: { sx: { fontSize: '0.8125rem', fontWeight: childActive ? 500 : 400, color: 'inherit' } } }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Typography
        component="p"
        sx={{
          flexShrink: 0,
          m: 0,
          px: collapsed ? 0.5 : 2,
          pt: 1,
          pb: 1.5,
          color: appColors.sidebar.textMuted,
          fontSize: collapsed ? '0.625rem' : '0.75rem',
          fontWeight: 600,
          letterSpacing: collapsed ? '-0.03em' : 0,
          lineHeight: 1.25,
          textAlign: collapsed ? 'center' : 'left',
          whiteSpace: 'nowrap',
        }}
      >
        EEAS v1.2
      </Typography>
    </Box>
  );
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default SidebarNav;
